import { useEffect } from 'react';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EditorialMetaCard } from '@/components/content/EditorialMetaCard';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { contentPages } from '@/content/content-pages';
import { getEditorialMetadata } from '@/content/editorial-metadata';

type Props = {
  route: RouteDefinition;
};

export function ContentPage({ route }: Props) {
  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const content = contentPages[route.canonicalPath];
  const editorialMetadata = content?.editorial ?? getEditorialMetadata(route.canonicalPath);

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {content?.eyebrow ?? 'Resource'}
        </p>
        <h1 className="mt-2 font-display text-4xl leading-tight md:text-5xl">
          {content?.title ?? route.title}
        </h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          {content?.intro ?? route.description}
        </p>
      </section>

      {editorialMetadata ? <EditorialMetaCard metadata={editorialMetadata} /> : null}

      <section className="grid gap-4">
        {(content?.sections ?? []).map((section) => (
          <Card key={section.heading}>
            <CardHeader>
              <CardTitle>{section.heading}</CardTitle>
              <CardDescription>{section.body}</CardDescription>
            </CardHeader>
            {section.bullets && section.bullets.length > 0 ? (
              <CardContent>
                <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </CardContent>
            ) : null}
          </Card>
        ))}
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
    </AppLayout>
  );
}
